import { useState, useCallback, useRef } from 'react';

const BARRIER_CROSS_VARIANT = 'frontal_harassment_blue_cross';
const BARRIER_CROSS_STAGGER_MS = 120;

function isBarrierCrossAnimation(animationData) {
  return animationData?.deployAnimationVariant === BARRIER_CROSS_VARIANT;
}

/**
 * Hook to manage deploy targeting animations.
 * Tracks active animations and prevents duplicates.
 * Uses instanceId to ensure each deployment instance only plays its deploy VFX once
 * (e.g. unit 71/73 bullet) — see registerDeployTargetingAnimation.
 *
 * Frontal Harassment barrier damage uses a separate queue; it may repeat across turns
 * but each barrier threshold only plays once per session (playedBarrierCrossKeysRef).
 */
export const useDeployTargetAnimations = () => {
  const [activeAnimations, setActiveAnimations] = useState(new Map());
  /** Permanent deploy-once guard: instanceId + target (unit 70/71/73 deploy, Thoth, etc.) */
  const completedAnimationsRef = useRef(new Set());
  /** Barrier cross keys already played (per threshold); not the deploy instanceId key */
  const playedBarrierCrossKeysRef = useRef(new Set());
  const barrierCrossQueueRef = useRef([]);
  const barrierCrossPumpTimerRef = useRef(null);
  const barrierCrossPlayingRef = useRef(false);

  /**
   * Original deploy targeting registration — unchanged dedupe rules.
   * Each deployment instance → each target plays at most once for the battle UI session.
   */
  const registerDeployTargetingAnimation = useCallback((animationData) => {
    if (!animationData || !animationData.fromSlot || !animationData.toSlot) {
      console.warn('⚠️ useDeployTargetAnimations: Invalid animation data', animationData);
      return;
    }

    const animationKey =
      animationData.animationKey ||
      `${animationData.deployUnitInstanceId || 'unknown'}-${animationData.fromSlot.zone}-${animationData.fromSlot.index}-${animationData.toSlot.zone}-${animationData.toSlot.index}-${animationData.animationType}`;

    const instanceIdKey = animationData.instanceIdKeyForDedupe
      ? animationData.instanceIdKeyForDedupe
      : animationData.deployUnitInstanceId
        ? `instance-${animationData.deployUnitInstanceId}-${animationData.toSlot.zone}-${animationData.toSlot.index !== null ? animationData.toSlot.index : 'general'}-${animationData.toSlot.playerId || 'unknown'}`
        : null;

    setActiveAnimations((prev) => {
      if (prev.has(animationKey) || completedAnimationsRef.current.has(animationKey)) {
        console.log('⚠️ useDeployTargetAnimations: Animation already active or completed (by key)', animationKey);
        return prev;
      }

      if (instanceIdKey) {
        const existingByInstanceId = Array.from(prev.values()).find((anim) => {
          if (isBarrierCrossAnimation(anim)) {
            return false;
          }
          const animInstanceKey = anim.instanceIdKeyForDedupe
            ? anim.instanceIdKeyForDedupe
            : anim.deployUnitInstanceId
              ? `instance-${anim.deployUnitInstanceId}-${anim.toSlot.zone}-${anim.toSlot.index !== null ? anim.toSlot.index : 'general'}-${anim.toSlot.playerId || 'unknown'}`
              : null;
          return animInstanceKey === instanceIdKey;
        });

        if (existingByInstanceId) {
          console.log(
            '⚠️ useDeployTargetAnimations: Animation already active for this deployment instance',
            instanceIdKey
          );
          return prev;
        }

        if (completedAnimationsRef.current.has(instanceIdKey)) {
          console.log(
            '⚠️ useDeployTargetAnimations: Animation already completed for this deployment instance',
            instanceIdKey
          );
          return prev;
        }
      }

      console.log(`✅ useDeployTargetAnimations: Registering animation`, animationKey, {
        instanceId: animationData.deployUnitInstanceId,
        instanceIdKey,
      });

      const next = new Map(prev);
      next.set(animationKey, {
        ...animationData,
        key: animationKey,
        instanceIdKey,
        registeredAt: Date.now(),
      });
      return next;
    });
  }, []);

  const registerBarrierCrossAnimation = useCallback((animationData) => {
    const animationKey =
      animationData.animationKey ||
      `${animationData.deployUnitInstanceId || 'unknown'}-barrier-cross-${animationData.barrierThreshold ?? 'x'}`;

    if (playedBarrierCrossKeysRef.current.has(animationKey)) {
      console.log('⚠️ useDeployTargetAnimations: Barrier cross already played', animationKey);
      return false;
    }

    if (completedAnimationsRef.current.has(animationKey)) {
      return false;
    }

    let registered = false;
    setActiveAnimations((prev) => {
      if (prev.has(animationKey)) {
        return prev;
      }

      registered = true;
      const next = new Map(prev);
      next.set(animationKey, {
        ...animationData,
        key: animationKey,
        instanceIdKey: null,
        registeredAt: Date.now(),
      });
      return next;
    });
    return registered;
  }, []);

  const pumpBarrierCrossQueue = useCallback(() => {
    if (barrierCrossPlayingRef.current) {
      return;
    }
    const next = barrierCrossQueueRef.current.shift();
    if (!next) {
      return;
    }
    barrierCrossPlayingRef.current = true;
    const registered = registerBarrierCrossAnimation(next);
    if (!registered) {
      barrierCrossPlayingRef.current = false;
      barrierCrossPumpTimerRef.current = setTimeout(() => {
        barrierCrossPumpTimerRef.current = null;
        pumpBarrierCrossQueue();
      }, BARRIER_CROSS_STAGGER_MS);
    }
  }, [registerBarrierCrossAnimation]);

  const enqueueBarrierCrossAnimations = useCallback(
    (animations, options = {}) => {
      if (!Array.isArray(animations) || animations.length === 0) {
        return;
      }
      const sorted = [...animations]
        .filter((a) => a?.animationKey && !playedBarrierCrossKeysRef.current.has(a.animationKey))
        .sort((a, b) => (b.barrierThreshold || 0) - (a.barrierThreshold || 0));

      if (sorted.length === 0) {
        return;
      }

      barrierCrossQueueRef.current.push(...sorted);

      const delayMs = typeof options.delayMs === 'number' ? options.delayMs : 0;
      if (barrierCrossPumpTimerRef.current) {
        clearTimeout(barrierCrossPumpTimerRef.current);
        barrierCrossPumpTimerRef.current = null;
      }
      barrierCrossPumpTimerRef.current = setTimeout(() => {
        barrierCrossPumpTimerRef.current = null;
        pumpBarrierCrossQueue();
      }, Math.max(0, delayMs));
    },
    [pumpBarrierCrossQueue]
  );

  const registerAnimation = useCallback(
    (animationData) => {
      if (isBarrierCrossAnimation(animationData)) {
        enqueueBarrierCrossAnimations([animationData], { delayMs: 0 });
        return;
      }
      registerDeployTargetingAnimation(animationData);
    },
    [enqueueBarrierCrossAnimations, registerDeployTargetingAnimation]
  );

  const completeAnimation = useCallback(
    (animationKey) => {
      console.log(`✅ useDeployTargetAnimations: Completing animation`, animationKey);

      let wasBarrierCross = false;
      setActiveAnimations((prev) => {
        const animation = prev.get(animationKey);
        const next = new Map(prev);
        next.delete(animationKey);

        wasBarrierCross = isBarrierCrossAnimation(animation);

        if (wasBarrierCross) {
          playedBarrierCrossKeysRef.current.add(animationKey);
        } else if (animation && animation.instanceIdKey) {
          completedAnimationsRef.current.add(animation.instanceIdKey);
          console.log(
            `✅ useDeployTargetAnimations: Marked instanceId animation as completed (permanent)`,
            animation.instanceIdKey
          );
        }

        completedAnimationsRef.current.add(animationKey);
        setTimeout(() => {
          completedAnimationsRef.current.delete(animationKey);
        }, 100);

        return next;
      });

      if (wasBarrierCross) {
        barrierCrossPlayingRef.current = false;
        if (barrierCrossPumpTimerRef.current) {
          clearTimeout(barrierCrossPumpTimerRef.current);
        }
        barrierCrossPumpTimerRef.current = setTimeout(() => {
          barrierCrossPumpTimerRef.current = null;
          pumpBarrierCrossQueue();
        }, BARRIER_CROSS_STAGGER_MS);
      }
    },
    [pumpBarrierCrossQueue]
  );

  const reset = useCallback(() => {
    if (barrierCrossPumpTimerRef.current) {
      clearTimeout(barrierCrossPumpTimerRef.current);
      barrierCrossPumpTimerRef.current = null;
    }
    barrierCrossQueueRef.current = [];
    barrierCrossPlayingRef.current = false;
    playedBarrierCrossKeysRef.current.clear();
    completedAnimationsRef.current.clear();
    setActiveAnimations(new Map());
  }, []);

  return {
    activeAnimations: Array.from(activeAnimations.values()),
    registerAnimation,
    enqueueBarrierCrossAnimations,
    completeAnimation,
    reset,
  };
};
